import styled from 'styled-components';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import compositionsData from '../data/compositions.json';
import AudioPlayer from '../components/AudioPlayer/AudioPlayer';

gsap.registerPlugin(ScrollTrigger);

const MusicContainer = styled.div`
  width: 100%;
  padding: 120px 2rem 4rem;
  background-color: ${props => props.theme.colors.cream};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.title};
  color: ${props => props.theme.colors.black};
  text-align: center;
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: 3rem;
  opacity: 0;
`;

const MusicSection = styled.section`
  margin-bottom: 4rem;
  opacity: 0;
  transform: translateY(30px);
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.wood};
  font-size: clamp(1.5rem, 3vw, 2rem);
  margin-bottom: 2rem;
  text-align: center;
`;

const TrackList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const TrackCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const TrackTitle = styled.h3`
  font-family: ${props => props.theme.fonts.heading};
  color: ${props => props.theme.colors.black};
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const TrackDescription = styled.p`
  color: ${props => props.theme.colors.black};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const IframeContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 10px;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  border-radius: 10px;
  display: block;
  background: #000;
`;


function Music() {
  const titleRef = useRef(null);
  const sectionsRef = useRef([]);

  useEffect(() => {
    // Animation du titre
    gsap.to(titleRef.current, {
      opacity: 1,
      duration: 1,
      ease: 'power3.out'
    });

    // Animation des sections
    sectionsRef.current.forEach((section, i) => {
      gsap.to(section, {
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          toggleActions: 'play none none reverse'
        },
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: i * 0.2,
        ease: 'power2.out'
      });
    });
  }, []);

  return (
    <MusicContainer>
      <ContentWrapper>
        <PageTitle ref={titleRef}>Musique</PageTitle>

        <MusicSection ref={el => sectionsRef.current[0] = el}>
          <SectionTitle>Nos Compositions</SectionTitle>
          <TrackList>
            {compositionsData.compositions.map((composition) => (
              <TrackCard key={composition.id}>
                <TrackTitle>{composition.title}</TrackTitle>
                <TrackDescription>
                  {composition.description}
                </TrackDescription>
                {composition.soundCloudUrl ? (
                  <IframeContainer>
                    <iframe
                      src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(composition.soundCloudUrl)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
                      title={`SoundCloud Player - ${composition.title}`}
                      allow="autoplay"
                    ></iframe>
                  </IframeContainer>
                ) : composition.localFile && composition.type === 'video' ? (
                  <VideoPlayer
                    controls
                    src={composition.localFile}
                    title={composition.title}
                    preload="metadata"
                  />
                ) : composition.localFile && (
                  <AudioPlayer
                    src={composition.localFile}
                    title={composition.title}
                  />
                )}
              </TrackCard>
            ))}
          </TrackList>
        </MusicSection>
      </ContentWrapper>
    </MusicContainer>
  );
}

export default Music;
